import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { DecisionSummary, DocumentPulse } from "../../../models/home";
import { LanguageService } from "../../../services/language/language.service";
import { UxError } from "../../../models/ux-error.model";
import { SseState } from "../../../services/home-ia/home-stream.service";

@Component({
  selector: "app-home-decision-pulse-strip",
  templateUrl: "./home-decision-pulse-strip.component.html",
  styleUrls: ["./home-decision-pulse-strip.component.scss"],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomeDecisionPulseStripComponent {
  @Input() pulse: DocumentPulse | null = null;
  @Input() summary: DecisionSummary | null = null;
  @Input() connectionState: SseState | null = null;
  @Input() loading = false;
  @Input() error: UxError | null = null;

  constructor(public readonly lang: LanguageService) {}

  get tone(): "alert" | "watch" | "calm" {
    if ((this.summary?.actNow ?? 0) > 0 || (this.pulse?.overdue ?? 0) > 0) {
      return "alert";
    }

    if ((this.summary?.actSoon ?? 0) > 0 || this.pulse?.systemState === "attention") {
      return "watch";
    }

    return "calm";
  }

  get headline(): string {
    if (this.tone === "alert") {
      return this.lang.translate("home.decisionPulse.headline.alert");
    }

    if (this.tone === "watch") {
      return this.lang.translate("home.decisionPulse.headline.watch");
    }

    return this.lang.translate("home.decisionPulse.headline.calm");
  }

  get supportCopy(): string {
    const actNow = this.summary?.actNow ?? 0;
    const actSoon = this.summary?.actSoon ?? 0;
    const overdue = this.pulse?.overdue ?? 0;
    const total = this.summary?.total ?? 0;

    if (actNow > 0) {
      const parts = [`${actNow} ${this.lang.translate("home.decisionPulse.copy.actNow")}`];
      if (overdue > 0) {
        parts.push(`${overdue} ${this.lang.translate("home.decisionPulse.copy.overdue")}`);
      }
      return parts.join(" · ");
    }

    if (actSoon > 0) {
      return `${actSoon} ${this.lang.translate("home.decisionPulse.copy.actSoon")}`;
    }

    if (total > 0) {
      return `${total} ${this.lang.translate("home.decisionPulse.copy.monitoring")}`;
    }

    return this.lang.translate("home.decisionPulse.copy.empty");
  }

  get connectionLabel(): string | null {
    if (!this.connectionState) {
      return null;
    }

    return this.lang.translate(`home.decisionPulse.connection.${this.connectionState}`);
  }
}